import moment from 'moment'

// The LMS insights pages' filters and how they go on the wire: the analytics
// service's /api/v1/lms-insight routes. Every launch report and export takes
// the same start, end, interval, tz, connection and course.

export const INSIGHT_BASE = 'api/v1/lms-insight'

// The series intervals, with the longest range each covers (the service sends
// the same list on /details; a day at a time covers 186 days, the rest 731).
export const MAX_RANGE_DAYS = 731

export const DEFAULT_INTERVALS = [
  { id: 'day1', maxDays: 186 },
  { id: 'week1', maxDays: MAX_RANGE_DAYS },
  { id: 'week2', maxDays: MAX_RANGE_DAYS },
  { id: 'month1', maxDays: MAX_RANGE_DAYS },
  { id: 'month3', maxDays: MAX_RANGE_DAYS },
  { id: 'month6', maxDays: MAX_RANGE_DAYS },
  { id: 'year1', maxDays: MAX_RANGE_DAYS }
]

export const INTERVAL_TEXT = {
  day1: '1 day',
  week1: '1 week',
  week2: '2 weeks',
  month1: '1 month',
  month3: '3 months',
  month6: '6 months',
  year1: '1 year'
}

const DAY = 'YYYY-MM-DD'

// The zone the pages read dates in where the browser names none, or names one
// the service does not know.
export const FALLBACK_TIME_ZONE = 'UTC'

// The browser's IANA time zone, which the service reads every date in
// ("tz"); UTC where the browser names none.
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE
  } catch (e) {
    return FALLBACK_TIME_ZONE
  }
}

// The last 30 days ending today (both ends count), a day at a time, for every
// connection and course. (now: a moment for "today", the clock's by default.)
export const defaultFilters = now => ({
  start: moment(now).subtract(29, 'days').format(DAY),
  end: moment(now).format(DAY),
  interval: 'day1',
  tz: browserTimeZone(),
  connection: '',
  course: ''
})

// The days from start to end, both counted.
export const dayCount = (start, end) => moment.utc(end, DAY).diff(moment.utc(start, DAY), 'days') + 1

export const allowedIntervals = (days, intervals = DEFAULT_INTERVALS) => intervals.filter(interval => interval.maxDays >= days)

// The interval the reports are asked for: the one chosen while it covers the
// range, else the shortest that does (a year of days is 52 weeks, not 365 bars).
export const effectiveInterval = (chosen, days, intervals = DEFAULT_INTERVALS) => {
  const allowed = allowedIntervals(days, intervals)
  if (allowed.some(interval => interval.id === chosen)) return chosen
  return allowed.length ? allowed[0].id : chosen
}

// What is wrong with a range, in a sentence, or null.
export const rangeProblem = (start, end, maxDays = MAX_RANGE_DAYS) => {
  const days = dayCount(start, end)
  if (days < 1) return 'The end date is before the start date.'
  if (days > maxDays) return `Choose a range of ${maxDays} days or less.`
  return null
}

// A zone name keeps its slash (it is allowed in a query, and America/New_York
// reads better than America%2FNew_York).
const encode = value => encodeURIComponent(value).replace(/%2F/g, '/')

// The query every launch report takes, without a leading "?". A connection and
// a course go on it only when one is chosen.
export const launchQuery = ({ start, end, interval, tz, connection, course }) => {
  const parts = [`start=${encode(start)}`, `end=${encode(end)}`, `interval=${encode(interval)}`, `tz=${encode(tz)}`]
  if (connection) parts.push(`connection=${encode(connection)}`)
  if (course) parts.push(`course=${encode(course)}`)
  return parts.join('&')
}

// What the filter bar offers for a connection: the zone and the connection.
export const detailsQuery = ({ tz, connection }) => {
  const parts = [`tz=${encode(tz)}`]
  if (connection) parts.push(`connection=${encode(connection)}`)
  return parts.join('&')
}

// The report's download: format=csv|ndjson, then the report's own filters.
export const exportEndpoint = (report, filters, format) => `${INSIGHT_BASE}/launches/${report}/export?format=${format}&${launchQuery(filters)}`

// Whether a bucket (its bucketStart, RFC 3339) begins before the cutoff
// (eventsRetainedFrom or countsRetainedFrom, RFC 3339 UTC, null when nothing
// has been purged). What began before it is not kept in full.
export const beforeCutoff = (bucketStart, cutoff) => {
  if (!cutoff) return false
  const start = Date.parse(bucketStart)
  const limit = Date.parse(cutoff)
  return Number.isFinite(start) && Number.isFinite(limit) && start < limit
}

// The later of the cutoffs there are (null where there is none): what began
// before it is missing some of what the cutoffs keep.
export const laterCutoff = (...cutoffs) =>
  cutoffs
    .filter(cutoff => cutoff && Number.isFinite(Date.parse(cutoff)))
    .reduce((later, cutoff) => (later === null || Date.parse(cutoff) > Date.parse(later) ? cutoff : later), null)

// A cutoff as the date it falls on in the chosen zone (MM/DD/YYYY, as the
// date pickers write dates).
export const formatCutoff = (cutoff, tz) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' }
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(new Date(cutoff))
  } catch (e) {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(new Date(cutoff))
  }
}
